import React, { useCallback, useEffect } from 'react';
import { Button, Card, Form, Row, Tab, Tabs } from 'react-bootstrap';
import { useHistory, useLocation } from 'react-router-dom';
import BreadCrumbWrapper from 'components/global/BreadCrumbs/BreadCrumbs';
import {
  fetchAndSetBusinessById,
  submitBusinessForEdo,
} from 'components/views/Business/BusinessSettings/BusinessSettings.helpers';
import BusinessBasicInfo from 'components/views/Business/BusinessSettings/components/BusinessBasicInfo';
import BusinessMinorityStatus from 'components/views/Business/BusinessSettings/components/BusinessMinorityStatus';
import BusinessFinance from 'components/views/Business/BusinessSettings/components/BusinessFinance';
import BusinessAssistance from 'components/views/Business/BusinessSettings/components/BusinessAssistance';
import BusinessDemographics from 'components/views/Business/BusinessSettings/components/BusinessDemographics';
import BusinessDiscovery from 'components/views/Business/BusinessSettings/components/BusinessDiscovery';
import { organizationDetailsSignal } from 'components/global/OrganizationsList/OrganizationsList.signal';
import {
  businessSettingsDirtySignal,
} from 'signals/BusinessSettings.signal';

function SectionWrapper({ children, onSubmit, onCancel }) {
  return (
    <div className="bg-white p-32 border border-gray shadow-sm">
      <Form onSubmit={onSubmit}>
        {children}
        <hr />
        <Row className="px-16 justify-content-end">
          <Button
            type="button"
            variant="outline-secondary"
            className="rounded mr-2"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button type="submit" className="rounded">
            Save
          </Button>
        </Row>
      </Form>
    </div>
  );
}

const EdoBusinessEdit = () => {
  const history = useHistory();
  const { pathname, search } = useLocation();
  const tab = new URLSearchParams(search).get('tab') || 'company-info';
  const businessId = pathname.split('/')[3];
  const { selectedOrganization } = organizationDetailsSignal.value;

  const init = useCallback(async () => {
    await fetchAndSetBusinessById(businessId);
  }, [businessId]);

  useEffect(() => {
    init();
    return () => {
      businessSettingsDirtySignal.reset();
    };
  }, [init]);

  const handleTabChange = useCallback((nextTab) => {
    history.replace(`/edo/business/${businessId}/edit?tab=${nextTab}`);
  }, [businessId, history]);

  const handleCancel = useCallback(() => {
    businessSettingsDirtySignal.reset();
    history.push(`/edo/business/${businessId}`);
  }, [businessId, history]);

  const handleSubmit = useCallback((event) => {
    submitBusinessForEdo(event, businessId, () => {
      history.push(`/edo/business/${businessId}`);
    });
  }, [businessId, history]);

  const labels = ['', selectedOrganization?.name || '', 'Edit'];

  return (
    <BreadCrumbWrapper labels={labels}>
      <Card>
        <Card.Body>
          <Tabs
            defaultActiveKey="company-info"
            activeKey={tab}
            animation="false"
            id="edo-business-edit-tabs"
            onSelect={handleTabChange}
          >
            <Tab eventKey="company-info" title="General Info">
              <SectionWrapper onSubmit={handleSubmit} onCancel={handleCancel}>
                <BusinessBasicInfo />
              </SectionWrapper>
            </Tab>
            <Tab eventKey="sediAndMinorityStatus" title="SEDI & Minority Status">
              <SectionWrapper onSubmit={handleSubmit} onCancel={handleCancel}>
                <BusinessMinorityStatus />
              </SectionWrapper>
            </Tab>
            <Tab eventKey="finance" title="Finance">
              <SectionWrapper onSubmit={handleSubmit} onCancel={handleCancel}>
                <BusinessFinance />
              </SectionWrapper>
            </Tab>
            <Tab eventKey="assistance" title="Assistance">
              <SectionWrapper onSubmit={handleSubmit} onCancel={handleCancel}>
                <BusinessAssistance />
              </SectionWrapper>
            </Tab>
            <Tab eventKey="demographics" title="Demographics">
              <SectionWrapper onSubmit={handleSubmit} onCancel={handleCancel}>
                <BusinessDemographics />
              </SectionWrapper>
            </Tab>
            <Tab eventKey="discovery" title="Discovery">
              <SectionWrapper onSubmit={handleSubmit} onCancel={handleCancel}>
                <BusinessDiscovery />
              </SectionWrapper>
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>
    </BreadCrumbWrapper>
  );
};

export default EdoBusinessEdit;
